import React, { useState, useEffect } from 'react';
import { getToken } from '../../../../services/api';

const DistributionModal = ({ estado, onClose, onDistributionComplete }) => {
  const [assignments, setAssignments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [distributing, setDistributing] = useState(false);
  const [error, setError] = useState(null);
  const [distributionPreview, setDistributionPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchData();
  }, [estado]);

  // Remove automatic preview calculation
  // useEffect(() => {
  //   if (selectedMembers.length > 0 && assignments.length > 0) {
  //     fetchDistributionPreview();
  //   } else {
  //     setDistributionPreview(null);
  //   }
  // }, [selectedMembers, assignments]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      
      // Fetch both precatorios assigned to gestor and team members in parallel
      const [assignmentsRes, teamRes] = await Promise.all([
        fetch(`${API_URL}/comercial-assignments/gestor/precatorios/${estado}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/comercial-assignments/gestor/team`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!assignmentsRes.ok || !teamRes.ok) {
        throw new Error('Erro ao buscar dados');
      }

      const assignmentsData = await assignmentsRes.json();
      const teamData = await teamRes.json();

      setAssignments(assignmentsData);
      setTeamMembers(teamData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDistributionPreview = async () => {
    try {
      setLoadingPreview(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      
      const response = await fetch(`${API_URL}/comercial-assignments/gestor/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assignmentIds: assignments.map(a => a._id),
          teamMemberIds: selectedMembers,
          estado: estado
        })
      });

      if (response.ok) {
        const preview = await response.json();
        setDistributionPreview(preview);
      }
    } catch (err) {
      console.error('Error fetching preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleMemberToggle = (memberId) => {
    setSelectedMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
    // Clear preview when selection changes
    setDistributionPreview(null);
  };

  const handleCalculateDistribution = () => {
    if (selectedMembers.length > 0 && assignments.length > 0) {
      fetchDistributionPreview();
    }
  };

  const handleDistribute = async () => {
    if (selectedMembers.length === 0 || assignments.length === 0) return;

    try {
      setDistributing(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      
      const response = await fetch(`${API_URL}/comercial-assignments/gestor/distribute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assignmentIds: assignments.map(a => a._id),
          teamMemberIds: selectedMembers,
          estado: estado
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Erro ao distribuir precatórios');
      }

      const result = await response.json();
      alert(result.message);
      onDistributionComplete();
    } catch (err) {
      alert('Erro: ' + err.message);
    } finally {
      setDistributing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  return (
    <div className="distribution-modal-overlay" onClick={onClose}>
      <div className="distribution-modal" onClick={(e) => e.stopPropagation()}>
        <div className="distribution-modal-header">
          <h2>Distribuir Precatórios - {estado}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="distribution-modal-body">
          {loading ? (
            <div className="loading">Carregando...</div>
          ) : error ? (
            <div className="error">Erro: {error}</div>
          ) : (
            <>
              <div className="precatorios-info">
                <p><strong>{assignments.length}</strong> precatórios atribuídos a você para distribuição</p>
                <p>Valor total: <strong>{formatCurrency(assignments.reduce((sum, a) => sum + (a.valorAtualizado || 0), 0))}</strong></p>
                
                {assignments.length > 0 && (
                  <div className="precatorios-list">
                    {assignments.slice(0, 5).map((assignment, index) => (
                      <div key={assignment._id} className="precatorio-item">
                        <strong>{assignment.numeroPrecatorio}</strong> - {formatCurrency(assignment.valorAtualizado)}
                        <span className="assignment-date">
                          Atribuído em {new Date(assignment.assignedToGestor.assignedAt).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    ))}
                    {assignments.length > 5 && (
                      <div className="precatorio-item">
                        ... e mais {assignments.length - 5} precatórios
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="team-section">
                <h3>Selecione os membros da equipe:</h3>
                {teamMembers.length === 0 ? (
                  <p>Nenhum membro disponível</p>
                ) : (
                  <div className="team-list">
                    {teamMembers.map(member => (
                      <label key={member._id} className={`team-member-item ${selectedMembers.includes(member._id) ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedMembers.includes(member._id)}
                          onChange={() => handleMemberToggle(member._id)}
                        />
                        <div className="member-info">
                          <span className="name">{member.name}</span>
                          <span className="email">{member.email}</span>
                          {member.isGestor && <span className="tag">Você</span>}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {selectedMembers.length > 0 && (
                <div className="calculate-button-container">
                  <button 
                    className="btn btn-calculate" 
                    onClick={handleCalculateDistribution}
                    disabled={loadingPreview}
                  >
                    {loadingPreview ? 'Calculando...' : 'Calcular Distribuição'}
                  </button>
                </div>
              )}

              {distributionPreview && !loadingPreview && (
                <div className="distribution-preview">
                  <h3>Prévia da Distribuição:</h3>
                  <div className="preview-content">
                    <p className="total-info">
                      <strong>{distributionPreview.totalPrecatorios}</strong> precatórios em <strong>{distributionPreview.totalGroups}</strong> grupos de CPF/CNPJ
                    </p>
                    <div className="preview-list">
                      {distributionPreview.preview.map((item, index) => (
                        <div key={item.memberId} className="preview-item">
                          <span className="member-name">{item.memberName}</span>
                          <div className="distribution-details">
                            <span className="precatorio-count">
                              <strong>{item.precatorioCount}</strong> precatórios
                            </span>
                            <span className="group-count">
                              ({item.groupCount} grupos)
                            </span>
                            <span className="total-value">
                              {formatCurrency(item.totalValue)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {distributionPreview.preview.length > 0 && (
                      <div className="distribution-note">
                        <p>
                          <strong>Nota:</strong> A distribuição busca equalizar o número de precatórios entre os membros, 
                          respeitando o agrupamento por CPF/CNPJ.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}


            </>
          )}
        </div>

        <div className="distribution-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={distributing}>
            Cancelar
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleDistribute}
            disabled={selectedMembers.length === 0 || assignments.length === 0 || distributing}
          >
            {distributing ? 'Distribuindo...' : 'Distribuir para Equipe'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DistributionModal;
